import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  useCallback,
} from "react";
import Draggable from "react-draggable";
import "../App.css";
import RiverSensorChart from "./RiverSensorChart";
import { LocationMarkerPopup } from "../mainLayer/MainWindow";
import RiverSensorLocation from "./RiverSensorLocation";
import AvailableRiverSensors from "./AvailableRiverSensors";
import { ResizableBox, ResizeCallbackData } from "react-resizable";
import { map } from "../mainLayer/MainLayer";
import { openWindowsMapRiver, riverMap } from "./RiverSensors";
import { Coordinate } from "ol/coordinate";
import Overlay from "ol/Overlay";
import MainForcastChart from "./MainForcastChart";
import loadImageBlue from "../assets/ai-load-blue.gif";
import loadImageWhite from "../assets/ai-load-white.gif";
import { Paper } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid/DataGrid";
import { GridColDef } from "@mui/x-data-grid";

interface Props {
  sensorId: string;
  name: string;
  cautionLevel: number;
  warningLevel: number;
  criticalLevel: number;
  onClose: (incidentId: string) => void;
  addItem?: (id: string, type: "ew" | "river" | "air" | "hz" | "rain") => void;
  id?: string;
}

type SetupDateType = {
  startDate: Date;
  endDate: Date;
};

const columns: GridColDef[] = [
  { field: "id", headerName: "Id", flex: 1 },
  { field: "name", headerName: "Id", flex: 1 },
  { field: "value", headerName: "Value", flex: 1 },
  { field: "timestamp", headerName: "Timestamp", flex: 1 },
];

const SensorsPopUp: React.FC<Props> = (props) => {
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [level, setLevel] = useState<number | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [coordinates, setCoordinates] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [showSensorDetails, setShowSensorDetails] = useState(false);
  const [showAddSensor, setShowAddSensor] = useState(false);
  const [showTimeRangeOverlay, setShowTimeRangeOverlay] =
    useState<boolean>(false);
  const [showAddSensorOverlay, setShowAddSensorOverlay] =
    useState<boolean>(false);
  const [newRiverData, setNewRiverData] = useState<any>("");
  const [riverName, setRiverName] = useState<string>("");
  const [prevRiverData, setPrevRiverData] = useState<any[]>([]);
  const [isLoadingModel, setIsLoadingModel] = useState<boolean>(true);
  const [selectedModel, setSelectedModel] = useState("LSTM");
  const [activeFullScreen, setActiveFullScreen] = useState<boolean>(false);
  const fullscreenModal = useRef<HTMLDivElement | null>(null);
  const parentContainerRef = useRef<HTMLDivElement | null>(null);

  const [dimensions, setDimensions] = useState({
    resizableBoxwidth: 0,
    resizableBoxheight: 0,
  });

  const [dimensionsMin, setDimensionsMin] = useState({
    resizableBoxMinwidth: 0,
    resizableBoxMinheight: 0,
  });

  const updateDimensions = () => {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    const vh = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    );

    // setDimensions({
    //   resizableBoxwidth: vw * 0.68,
    //   resizableBoxheight: vh * 0.75,
    // });

    // setDimensionsMin({
    //   resizableBoxMinwidth: vw * 0.4,
    //   resizableBoxMinheight: vh * 0.72,
    // });

    if (vw > 2000 && vh > 1200) {
      setDimensions({
        resizableBoxwidth: vw * 0.4,
        resizableBoxheight: vh * 0.4,
      });
    } else {
      setDimensions({
        resizableBoxwidth: vw * 0.3,
        resizableBoxheight: vh * 0.6,
      });
    }

    if (vw > 2000 && vh > 1200) {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.3,
        resizableBoxMinheight: vh * 0.4,
      });
    } else {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.3,
        resizableBoxMinheight: vh * 0.4,
      });
    }
  };

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const openContentFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen();
      setShowSensorDetails(true);
    } else {
      exitFullScreen();
      setShowSensorDetails(false);
    }
  };

  const enterFullscreen = () => {
    const elem = fullscreenModal.current;
    if (elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      }
      setActiveFullScreen(true);
    }
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
    setActiveFullScreen(false);
  };

  const NewRiverData = (data: any) => {
    setNewRiverData(data);
  };

  const handleCustomValueShow = () => {
    setShowSensorDetails((current) => !current);
  };

  const handleAddSensor = () => {
    setShowAddSensor((current) => !current);
  };

  useEffect(() => {
    const fetchCurrentSensorsData = async () => {
      const RIVER_LEVEL_DATA_URL = process.env.REACT_APP_RIVER_LEVEL_DATA_URL;

      try {
        const response = await fetch(
          `${RIVER_LEVEL_DATA_URL}` + props.sensorId
        );
        const responseData = await response.json();

        if (responseData && typeof responseData === "object") {
          const { level, time, coordinates } = responseData;
          setLevel(level);
          setLastUpdate(time);
          setCoordinates(coordinates);
        } else {
          console.error("Invalid response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchCurrentSensorsData();
    return () => {};
  }, [props.sensorId]);

  const date = new Date().toLocaleDateString();
  const time = new Date().toLocaleTimeString();

  const hideDialog = (incidentId: string) => {
    setShowDialog(false);
    LocationMarkerPopup("baseMap");
    props.onClose(incidentId);
    removeOverlay(incidentId);
    if (riverMap.has(incidentId)) {
      riverMap.delete(incidentId);
      openWindowsMapRiver.set(incidentId, "close");
    }
    const entries = Array.from(riverMap.values());
    const lastEntryIndex = entries.length - 1;
    entries.forEach((details, index) => {
      const { coordinates, sensorId } = details;
      if (coordinates) {
        removeOverlay(sensorId!);
        if (index === lastEntryIndex) {
          addOverlayClickItemLast(sensorId!, coordinates);
        } else {
          addOverlayClickItem(sensorId!, coordinates);
        }
      }
    });
  };

  const removeOverlay = (key: string) => {
    const overlays = map.getOverlays().getArray();
    const overlaysToRemove = overlays.filter(
      (overlay) => overlay.getId() === key
    );
    overlaysToRemove.forEach((overlay) => {
      map.removeOverlay(overlay);
    });
  };

  const addOverlayClickItem = (id: string, coordinates: Coordinate) => {
    const markerElement = document.createElement("div");
    markerElement.className = "location-dot animate";

    const ringElement = document.createElement("div");
    ringElement.className = "location-dot-ring";

    markerElement.appendChild(ringElement);
    const overlay = new Overlay({
      id: id,
      position: coordinates,
      positioning: "center-center",
      element: markerElement,
      stopEvent: false,
    });
    map.addOverlay(overlay);
  };

  const addOverlayClickItemLast = (id: string, coordinates: Coordinate) => {
    const markerElement = document.createElement("div");
    markerElement.className = "locationlast-dot animate";
    const ringElement = document.createElement("div");
    ringElement.className = "locationlast-dot-ring";

    markerElement.appendChild(ringElement);
    const overlay = new Overlay({
      id: id,
      position: coordinates,
      positioning: "center-center",
      element: markerElement,
      stopEvent: false,
    });
    map.addOverlay(overlay);
  };

  const toggleTimeRangeOverlay = () => {
    setShowTimeRangeOverlay((prevState) => !prevState);
  };

  const closeTimeRangeOverlay = () => {
    setShowTimeRangeOverlay(false);
  };

  const toggleAddSensorOverlay = () => {
    setShowAddSensorOverlay((prevState) => !prevState);
  };

  const closeAddSensorOverlay = () => {
    setShowAddSensorOverlay(false);
  };

  const [widthMain, setWidthMain] = useState<number>(0);
  const [heightMain, setHeightMain] = useState<number>(0);

  useEffect(() => {}, [widthMain, heightMain]);

  const handleResize = (
    event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    setWidthMain(data.size.width);
    setHeightMain(data.size.height);
  };

  const [selectedTab, setSelectedTab] = useState<string>("realtime_data_tab");
  // Setup date
  const [setupDate, setSetupDate] = useState<SetupDateType | null>(null);
  // Selected gauges
  const [selectedGauges, setSelectedGauges] = useState<string[]>([]);
  // Sensor info open status
  const [isSensorInfoOpen, setIsSensorInfoOpen] = useState<boolean>(false);
  // Forecast selected model
  const [selectedForecastModel, setSelectedForecastModel] = useState<string>(
    "forecast_model_ecmwf"
  );

  // Input time values
  // Handle reset button click
  const handleResetButtonClick = () => {
    const startTimeInputElement = document.getElementById(
      "side_menu_start_date_input"
    ) as HTMLInputElement;
    const endTimeInputElement = document.getElementById(
      "side_menu_end_date_input"
    ) as HTMLInputElement;

    if (startTimeInputElement && endTimeInputElement) {
      setSetupDate((prevState) => null);
      startTimeInputElement.value = "";
      endTimeInputElement.value = "";
    }
  };

  // Handle set time button click
  const handleSetTimeButtonClick = () => {
    const startTimeInputElement = document.getElementById(
      "side_menu_start_date_input"
    ) as HTMLInputElement;
    const endTimeInputElement = document.getElementById(
      "side_menu_end_date_input"
    ) as HTMLInputElement;

    if (startTimeInputElement && endTimeInputElement) {
      const tempTime: SetupDateType = {
        startDate: new Date(startTimeInputElement.value),
        endDate: new Date(endTimeInputElement.value),
      };
      setSetupDate((prevState) => tempTime);
    }
  };

  const popupRef = useRef<HTMLDivElement | null>(null);
  const enteredRef = useRef<boolean>(false);

  const mouseMoveEvent = useCallback((event: MouseEvent) => {
    const sideMenu = document.getElementById("side-menu-wrapper");

    if (sideMenu && !enteredRef.current) {
      const sideMenuWidth = sideMenu.getBoundingClientRect();
      const sideMenuLeft = sideMenuWidth.left;

      if (sideMenuLeft < event.clientX) {
        enteredRef.current = true;
        if (props.id && props.addItem) {
          props.addItem(props.id, "river");
          /* props.windowState(false) */
          setShowDialog(false);
        }
      }
    }
  }, []);

  const touchMoveEvent = useCallback((event: TouchEvent) => {
    const sideMenu = document.getElementById("side-menu-wrapper");

    if (sideMenu && !enteredRef.current) {
      const sideMenuWidth = sideMenu.getBoundingClientRect();
      const sideMenuLeft = sideMenuWidth.left;

      const touchX = event.touches[0].clientX;

      if (sideMenuLeft < touchX) {
        enteredRef.current = true;
        if (props.id && props.addItem) {
          props.addItem(props.id, "river");
          /* props.windowState(false) */
          setShowDialog(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleMouseDown = () => {
      document.addEventListener("mousemove", mouseMoveEvent);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const handleTouchStart = () => {
      document.addEventListener("touchmove", touchMoveEvent);
      document.addEventListener("touchend", handleTouchEnd);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
    };

    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", touchMoveEvent);
    };

    if (popupRef.current) {
      popupRef.current.addEventListener("mouseenter", () => {
        popupRef.current?.addEventListener("mousedown", handleMouseDown);
        popupRef.current?.addEventListener("touchstart", handleTouchStart);
      });
    }

    return () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", touchMoveEvent);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [mouseMoveEvent, touchMoveEvent]);

  const handleDataLoaded = (loaded: boolean) => {
    setIsLoadingModel(loaded);
  };

  useEffect(() => {}, [isLoadingModel]);

  const [value, setValues] = useState<
    { id: number; name: string; value: number; timestamp: string }[]
  >([]);

  const handleModelChange = (model: string) => {
    setSelectedModel(model);
  };

  useEffect(() => {}, [selectedModel]);

  return (
    <div
      className="ol-overlay-container ol-selectable"
      style={{
        width: "10px",
        height: "10px",
      }}
    >
      <div
        style={{ visibility: showDialog ? "visible" : "hidden" }}
        ref={parentContainerRef}
      >
        <Draggable
          handle=".drag-handle"
          cancel=".no-drag"
          enableUserSelectHack={false}
        >
          <ResizableBox
            className="custom-resizable-box"
            width={dimensions.resizableBoxwidth}
            height={dimensions.resizableBoxheight}
            minConstraints={[
              dimensionsMin.resizableBoxMinwidth,
              dimensionsMin.resizableBoxMinheight,
            ]}
            /* maxConstraints={[800, 600]} */
            onResize={handleResize}
            resizeHandles={["se", "sw", "ne", "nw"]}
            style={{ zIndex: 1, left: "-30vw", right: "0vw", top: "-40vh" }}
          >
            <div
              className="drag-handle"
              ref={fullscreenModal}
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                cursor: "move",
                touchAction: "none",
              }}
            >
              <div className={`sensorPopupContainer`}>
                {/* Title container */}
                <div className={`titleContainer`} ref={popupRef}>
                  <span>river level</span>
                  <div className={`titleButtonContainer`}>
                    <i
                      className={`titleIcon fullScreenIco`}
                      onClick={openContentFullscreen}
                      onTouchStart={openContentFullscreen}
                    ></i>
                    <i
                      className={`titleIcon closeIco`}
                      onClick={() => hideDialog(props.sensorId)}
                      onTouchEnd={() => hideDialog(props.sensorId)}
                    ></i>
                  </div>
                </div>
                {/* Tab area */}
                <div className={`tabArea`}>
                  <div
                    className={`tabButton ${
                      selectedTab === "realtime_data_tab" ? "tabSelected" : ""
                    }`}
                    id="realtime_data_tab"
                    onClick={(e) =>
                      setSelectedTab((prevState) => "realtime_data_tab")
                    }
                    onTouchStart={(e) =>
                      setSelectedTab((prevState) => "realtime_data_tab")
                    }
                  >
                    realtime data
                  </div>
                  <div
                    className={`tabButton ${
                      selectedTab === "forecast_tab" ? "tabSelected" : ""
                    }`}
                    id="forecast_tab"
                    onClick={(e) =>
                      setSelectedTab((prevState) => "forecast_tab")
                    }
                  >
                    forecast
                  </div>
                </div>

                {/* Data container */}
                {selectedTab === "realtime_data_tab" && (
                  <div className={`tabRealTimeDataContainer`}>
                    <div className={`gridLayout`}>
                      {/* Chart */}
                      <div className={`chartContainer`}>
                        {/*  Chart hear */}
                        {activeFullScreen && (
                          <div
                            className="no-drag"
                            style={{
                              height: "40vh",
                              width: "80vw",
                              display: "flex",
                              alignItems: "start",
                              margin: "0px",
                              flexDirection: "column",
                              justifyContent: "top",
                            }}
                          >
                            <RiverSensorChart
                              id={props.sensorId}
                              name={props.name}
                              critical={props.criticalLevel}
                              cautious={props.cautionLevel}
                              warning={props.warningLevel}
                              riverData={newRiverData}
                              riverName={riverName}
                              fullScreen={activeFullScreen}
                              lastUpdate={lastUpdate}
                              showDialog={showDialog}
                              dimensions={dimensions}
                              chartWidth={widthMain * 0.9}
                              chartHeight={heightMain * 0.4}
                            />
                          </div>
                        )}
                        {!activeFullScreen && (
                          <div
                            className="no-drag"
                            style={{
                              height: "100%",
                              width: "95%",
                              display: "flex",
                              alignItems: "start",
                              margin: "0px",
                              flexDirection: "column",
                              justifyContent: "top",
                            }}
                          >
                            <RiverSensorChart
                              id={props.sensorId}
                              name={props.name}
                              critical={props.criticalLevel}
                              cautious={props.cautionLevel}
                              warning={props.warningLevel}
                              riverData={newRiverData}
                              riverName={riverName}
                              fullScreen={activeFullScreen}
                              lastUpdate={lastUpdate}
                              showDialog={showDialog}
                              dimensions={dimensions}
                              chartWidth={widthMain * 0.8}
                              chartHeight={heightMain * 0.5}
                            />
                          </div>
                        )}
                      </div>
                      {/* Side Menu 1 */}
                      <div className={`sideMenu1`}>
                        <span className={`title`}>Time</span>
                        <div className={`sideMenu1DataContainer`}>
                          {/* Display setup time */}
                          <div className={`currentDateDisplayContainer`}>
                            {setupDate !== null ? (
                              <>
                                <span>
                                  start time:
                                  <span>
                                    {new Date(
                                      setupDate.startDate
                                    ).toLocaleString()}
                                  </span>
                                </span>
                                <span>
                                  end time:
                                  <span>
                                    {new Date(
                                      setupDate.endDate
                                    ).toLocaleString()}
                                  </span>
                                </span>
                              </>
                            ) : (
                              <span>last 72 days</span>
                            )}
                          </div>
                          {/* Display date inputs */}
                          <div className={`inputContainer`}>
                            <label htmlFor="side_menu_start_date_input">
                              start date
                            </label>
                            <input
                              type="datetime-local"
                              id="side_menu_start_date_input"
                            />
                            <label htmlFor="side_menu_end_date_input">
                              end date
                            </label>
                            <input
                              type="datetime-local"
                              id="side_menu_end_date_input"
                            />
                          </div>
                          {/* Display button container */}
                          <div className={`buttonContainer`}>
                            <span
                              onClick={(e) => handleSetTimeButtonClick()}
                              onTouchStart={(e) => handleSetTimeButtonClick()}
                            >
                              <i className={`setTimeIco`}></i> set time
                            </span>
                            <span
                              onClick={(e) => handleResetButtonClick()}
                              onTouchStart={(e) => handleResetButtonClick()}
                            >
                              <i className={`setResetIco`}></i> reset
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Side Menu 2 */}
                      <div className={`sideMenu2`}>
                        <span className={`title`}>Gauges</span>
                        <AvailableRiverSensors
                          sendNewRiverData={NewRiverData}
                          setSharedString={setRiverName}
                        />
                      </div>
                      {/* Color */}
                      <div className={`color`}>
                        <div className={`legendItem`}>
                          <i
                            className={`colorBox`}
                            style={{ backgroundColor: "#0275d8" }}
                          ></i>
                          <span>river level</span>
                        </div>
                        <div className={`legendItem`}>
                          <i
                            className={`colorBox`}
                            style={{ backgroundColor: "#d9534f" }}
                          ></i>
                          <span>danger level</span>
                        </div>
                        <div className={`legendItem`}>
                          <i
                            className={`colorBox`}
                            style={{ backgroundColor: "#eb9824" }}
                          ></i>
                          <span>warning level</span>
                        </div>
                        <div className={`legendItem`}>
                          <i
                            className={`colorBox`}
                            style={{ backgroundColor: "#e4e736" }}
                          ></i>
                          <span>cautious level</span>
                        </div>
                      </div>
                      {/* Details */}
                      <div className={`detail`}>
                        <div className={`detailItem`}>
                          <span className={`label`}>Date & Time: </span>
                          <span className={`data`}>
                            {date}, {time}
                          </span>
                        </div>
                        <div className={`detailItem`}>
                          <span className={`label`}>Water Level: </span>
                          <span className={`data`}>{level} m</span>
                        </div>
                        <div className={`detailItem`}>
                          <span className={`label`}>Last Update: </span>
                          <span className={`data`}>{lastUpdate}</span>
                        </div>
                      </div>
                      {/* Sensor info expander */}
                      <div
                        className={`expander`}
                        onClick={(e) =>
                          setIsSensorInfoOpen((prevState) => !prevState)
                        }
                        onTouchStart={(e) =>
                          setIsSensorInfoOpen((prevState) => !prevState)
                        }
                      >
                        <i
                          className={`${
                            isSensorInfoOpen ? "expanderUp" : "expanderDown"
                          }`}
                        ></i>
                        <span>sensor details</span>
                      </div>
                      {/* Sensor info */}
                      {isSensorInfoOpen && (
                        <div className={`sensorInfoContainer`}>
                          <span className={`sensorInfoTitle`}>sensor info</span>
                          <div className={`sensorInfoDataContainer`}>
                            <div className={`left`}>
                              <div className={"dataRow"}>
                                <span className={"name"}>Name</span>
                                <span className={"data"}>{props.name}</span>
                              </div>

                              <div className={"dataRow"}>
                                <span className={"name"}>Cautious Level</span>
                                <span className={"data"}>
                                  {props.cautionLevel} m
                                </span>
                              </div>
                              <div className={"dataRow"}>
                                <span className={"name"}>Warning Level</span>
                                <span className={"data"}>
                                  {props.warningLevel} m
                                </span>
                              </div>
                              <div className={"dataRow"}>
                                <span className={"name"}>Danger Level</span>
                                <span className={"data"}>
                                  {props.criticalLevel} m
                                </span>
                              </div>
                            </div>
                            <div className={`right`}>
                              {/* Map here */}
                              <RiverSensorLocation
                                coordinates={
                                  coordinates || { latitude: 0, longitude: 0 }
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Forecast tab container */}
                {selectedTab === "forecast_tab" && (
                  <>
                    <label
                      htmlFor="model-select"
                      style={{ color: "var(--text-color)" }}
                    >
                      Select Model:{" "}
                    </label>
                    <select
                      id="model-select"
                      className="model-dropdown   no-drag"
                      onChange={(e) => handleModelChange(e.target.value)}
                      value={selectedModel}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "10px",
                        border: "2px solid #ccc",
                        backgroundColor: "var(--menu-color)",
                        fontSize: "12px",
                        marginLeft: "10px",
                        marginTop: "10px",
                        cursor: "pointer",
                        width: "100px",
                        color: "var(--text-color)",
                      }}
                    >
                      <option value="LSTM">LSTM</option>
                      <option value="FNN">FNN</option>
                    </select>
                    {(selectedModel === "LSTM" || selectedModel === "FNN") && (
                      <div className={`tabForecastContainer`}>
                        <div className={`forecastDataContainer`}>
                          <div className={`gridCell chart`}>
                            {activeFullScreen && (
                              <div
                                className="no-drag"
                                style={{
                                  height: "40vh",
                                  width: "80vw",
                                  display: "flex",
                                  alignItems: "start",
                                  margin: "0px",
                                  flexDirection: "column",
                                  justifyContent: "top",
                                }}
                              >
                                <MainForcastChart
                                  id={props.sensorId}
                                  name={props.name}
                                  critical={props.criticalLevel}
                                  cautious={props.cautionLevel}
                                  warning={props.warningLevel}
                                  riverData={newRiverData}
                                  riverName={riverName}
                                  fullScreen={activeFullScreen}
                                  lastUpdate={lastUpdate}
                                  showDialog={showDialog}
                                  dimensions={dimensions}
                                  chartWidth={widthMain * 0.9}
                                  chartHeight={heightMain * 0.4}
                                  onDataLoaded={handleDataLoaded}
                                  predictValues={setValues}
                                  selectedModel={selectedModel}
                                />
                              </div>
                            )}
                            {!activeFullScreen && (
                              <div
                                className="no-drag"
                                style={{
                                  height: "80%",
                                  width: "95%",
                                  display: "flex",
                                  alignItems: "start",
                                  margin: "0px",
                                  flexDirection: "column",
                                  justifyContent: "top",
                                }}
                              >
                                <MainForcastChart
                                  id={props.sensorId}
                                  name={props.name}
                                  critical={props.criticalLevel}
                                  cautious={props.cautionLevel}
                                  warning={props.warningLevel}
                                  riverData={newRiverData}
                                  riverName={riverName}
                                  fullScreen={activeFullScreen}
                                  lastUpdate={lastUpdate}
                                  showDialog={showDialog}
                                  dimensions={dimensions}
                                  chartWidth={widthMain * 0.9}
                                  chartHeight={heightMain * 0.7}
                                  onDataLoaded={handleDataLoaded}
                                  predictValues={setValues}
                                  selectedModel={selectedModel}
                                />
                              </div>
                            )}
                          </div>
                          <div className={`gridCell table`}>
                            <Paper sx={{ height: "100%", width: "100%" }}>
                              <DataGrid
                                rows={value}
                                columns={columns}
                                checkboxSelection={false}
                                columnHeaderHeight={50}
                                rowHeight={40}
                                disableColumnFilter
                                disableColumnSorting
                                disableColumnMenu
                                disableRowSelectionOnClick
                                disableColumnResize
                                columnVisibilityModel={{ id: false }}
                                sx={{
                                  border: 0,
                                  backgroundColor: "var(--background-color)",
                                  color:
                                    "var(--text-color)" /* borderRadius: '0.5rem', */,
                                  "& .MuiDataGrid-columnHeader": {
                                    backgroundColor: "var(--background-color)",
                                  },
                                  "& .MuiDataGrid-footerContainer": {
                                    display: "none", // Hide pagination bar
                                  },
                                }}
                              />
                            </Paper>
                          </div>
                        </div>

                        {isLoadingModel && (
                          <div className={`forecast-model-loading`}>
                            <img src={loadImageBlue} alt="" />
                            <span>Loading River Level Predictions...</span>
                            <span>
                              Eagle-I AI models are analyzing real-time data.
                              This may take a few moments.
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </ResizableBox>
        </Draggable>
      </div>
    </div>
  );
};

export default SensorsPopUp;
